<template>
  <div class="min-h-screen bg-gray-50">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div class="mb-6 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
        <h1 class="text-3xl font-bold text-gray-900">Convites para cadastro de hospedes</h1>
        <button
          type="button"
          :disabled="openCount >= maxOpen || loading"
          class="bg-indigo-600 text-white px-6 py-2.5 rounded-lg hover:bg-indigo-700 transition flex items-center justify-center space-x-2 shadow-sm select-none active:scale-[0.98] active:brightness-95 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
          @click="openModal"
        >
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
          </svg>
          <span>Gerar link</span>
        </button>
      </div>

      <div v-if="openCount >= maxOpen" class="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg text-amber-900 text-sm">
        Você atingiu o limite de {{ maxOpen }} convites em aberto. Aguarde uso, expiração (7 dias) ou exclua um convite
        para gerar outro.
      </div>

      <div class="bg-white rounded-lg shadow-sm mb-6">
        <button
          type="button"
          class="w-full flex items-center justify-between px-6 py-4 border-b border-gray-200 hover:bg-gray-50 transition"
          @click="showFilters = !showFilters"
        >
          <span class="text-sm font-semibold text-gray-800">Filtros</span>
          <svg
            class="w-5 h-5 text-gray-500 transform transition-transform"
            :class="showFilters ? 'rotate-180' : ''"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        <div v-if="showFilters" class="px-6 py-4">
          <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4 items-end">
            <div class="xl:col-span-1">
              <label class="block text-sm font-medium text-gray-700 mb-1">Nome do hospede</label>
              <input
                v-model.trim="filters.greeting_name"
                type="text"
                placeholder="Buscar por nome"
                class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                @keyup.enter="applyFilters"
              />
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select
                v-model="filters.status"
                class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white"
              >
                <option value="">Todos</option>
                <option value="aberto">Aberto</option>
                <option value="usado">Usado</option>
                <option value="expirado">Expirado</option>
              </select>
            </div>
            <div class="lg:col-span-1 xl:col-span-1">
              <label class="block text-sm font-medium text-gray-700 mb-1">Usuário (criador)</label>
              <select
                v-model="filters.user_id"
                class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white"
              >
                <option value="">Todos</option>
                <option v-for="u in filterUsers" :key="u.id" :value="String(u.id)">{{ u.label }}</option>
              </select>
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Data inicial criação</label>
              <input
                v-model="filters.created_from"
                type="date"
                class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Data final criação</label>
              <input
                v-model="filters.created_to"
                type="date"
                class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
            <div class="flex justify-end xl:justify-start">
              <button
                type="button"
                class="w-full xl:w-auto bg-indigo-600 text-white px-6 py-2 rounded-lg hover:bg-indigo-700 transition flex items-center justify-center space-x-2 shadow-sm"
                @click="applyFilters"
              >
                <svg class="w-5 h-5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <span>Buscar</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div v-if="loading" class="flex justify-center items-center h-64">
        <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>

      <div v-else-if="loadError" class="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
        {{ loadError }}
      </div>

      <div v-else class="bg-white rounded-lg shadow-sm overflow-hidden">
        <!-- Mobile -->
        <div class="sm:hidden">
          <div v-if="!invites.length" class="px-6 py-12 text-center text-gray-500">Nenhum registro encontrado</div>
          <div v-else class="divide-y divide-gray-200">
            <div v-for="row in invites" :key="row.id" class="p-4 bg-white">
              <div class="flex items-start justify-between gap-3">
                <div class="min-w-0">
                  <p class="text-sm font-semibold text-gray-900">{{ row.greeting_name }}</p>
                  <p class="text-xs text-gray-500 mt-1">{{ row.status }}</p>
                </div>
                <div class="flex items-center gap-2 flex-shrink-0">
                  <button
                    type="button"
                    class="text-indigo-600 hover:text-indigo-900 p-1"
                    title="Ver link e copiar"
                    @click="openLinkModal(row.id)"
                  >
                    <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                      />
                    </svg>
                  </button>
                  <button
                    type="button"
                    class="text-red-600 hover:text-red-900 p-1"
                    title="Excluir"
                    @click="openDeleteConfirm(row)"
                  >
                    <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                      />
                    </svg>
                  </button>
                </div>
              </div>
              <div class="mt-3 grid grid-cols-2 gap-x-4 gap-y-2 text-xs">
                <div class="text-gray-500">Criado por</div>
                <div class="text-gray-900 font-medium">{{ row.user?.label || '—' }}</div>
                <div class="text-gray-500">Data de criação</div>
                <div class="text-gray-900 font-medium">{{ formatDate(row.created_at) }}</div>
                <div class="text-gray-500">Expira</div>
                <div class="text-gray-900 font-medium">{{ formatDate(row.expires_at) }}</div>
                <div class="text-gray-500">Usado em</div>
                <div class="text-gray-900 font-medium">{{ row.used_at ? formatDate(row.used_at) : '—' }}</div>
              </div>
            </div>
          </div>
        </div>

        <!-- Desktop -->
        <div class="hidden sm:block overflow-x-auto">
          <table class="min-w-full divide-y divide-gray-200">
            <thead class="bg-gray-200">
              <tr>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                  Nome do hospede
                </th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Status</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Criado por</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Data de criação</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Expira</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Usado em</th>
                <th class="px-6 py-3 text-right text-xs font-medium text-gray-700 uppercase tracking-wider">Ações</th>
              </tr>
            </thead>
            <tbody class="bg-white divide-y divide-gray-200">
              <tr v-if="!invites.length">
                <td colspan="7" class="px-6 py-12 text-center text-gray-500">Nenhum registro encontrado</td>
              </tr>
              <tr v-for="row in invites" :key="row.id" class="hover:bg-gray-50 transition">
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-medium">{{ row.greeting_name }}</td>
                <td class="px-6 py-4 whitespace-nowrap text-sm">
                  <span
                    class="inline-flex px-2 py-0.5 rounded-full text-xs font-semibold"
                    :class="statusClass(row.status)"
                  >
                    {{ row.status }}
                  </span>
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{{ row.user?.label || '—' }}</td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{{ formatDate(row.created_at) }}</td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{{ formatDate(row.expires_at) }}</td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{{ row.used_at ? formatDate(row.used_at) : '—' }}</td>
                <td class="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <button
                    type="button"
                    class="text-indigo-600 hover:text-indigo-900 mr-4"
                    title="Ver link e copiar"
                    @click="openLinkModal(row.id)"
                  >
                    <svg class="w-5 h-5 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                      />
                    </svg>
                  </button>
                  <button type="button" class="text-red-600 hover:text-red-900" title="Excluir" @click="openDeleteConfirm(row)">
                    <svg class="w-5 h-5 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                      />
                    </svg>
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div
        v-if="!loading && !loadError && pagination.pages > 1"
        class="mt-4 bg-white rounded-lg shadow-sm border border-gray-100 px-4 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3"
      >
        <p class="text-sm text-gray-700">
          Mostrando
          <span class="font-medium">{{ pagination.from }}</span>
          a
          <span class="font-medium">{{ pagination.to }}</span>
          de
          <span class="font-medium">{{ pagination.count }}</span>
          resultados
        </p>
        <div class="flex gap-2 justify-end">
          <button
            type="button"
            :disabled="!pagination.prev"
            class="px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            @click="changePage(pagination.page - 1)"
          >
            Anterior
          </button>
          <button
            type="button"
            :disabled="!pagination.next"
            class="px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            @click="changePage(pagination.page + 1)"
          >
            Próxima
          </button>
        </div>
      </div>
    </div>

    <!-- Novo convite -->
    <div
      v-if="modalOpen"
      class="fixed inset-0 z-[80] flex items-center justify-center p-4 bg-black bg-opacity-50"
      @click.self="closeModal"
    >
      <div class="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
        <h2 class="text-lg font-semibold text-gray-900 mb-2">Novo convite</h2>
        <label class="block text-sm font-medium text-gray-700 mb-1">Nome do hospede *</label>
        <input
          v-model.trim="greetingName"
          type="text"
          maxlength="100"
          class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 mb-3"
        />
        <p v-if="modalError" class="text-sm text-red-600 mb-3">{{ modalError }}</p>
        <div class="flex justify-end gap-2 mt-4">
          <button
            type="button"
            class="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg shadow-sm transition select-none hover:bg-gray-50 active:scale-95 active:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-1"
            @click="closeModal"
          >
            Cancelar
          </button>
          <button
            type="button"
            :disabled="creating || !greetingName"
            class="px-4 py-2 bg-indigo-600 text-white rounded-lg shadow-sm transition select-none hover:bg-indigo-700 active:scale-95 active:brightness-95 disabled:opacity-50 disabled:pointer-events-none focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-1"
            @click="submitInvite"
          >
            {{ creating ? 'Gerando…' : 'Gerar' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Link / cópia -->
    <div
      v-if="resultModal.open"
      class="fixed inset-0 z-[80] flex items-center justify-center p-4 bg-black bg-opacity-50"
      @click.self="closeResult"
    >
      <div class="bg-white rounded-xl shadow-xl max-w-lg w-full p-6 space-y-4">
        <h2 class="text-lg font-semibold text-gray-900">Convite — {{ resultModal.greetingName }}</h2>
        <p v-if="resultModal.statusHint" class="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
          {{ resultModal.statusHint }}
        </p>
        <p v-if="resultModal.inviteStatus !== 'usado'" class="text-sm text-gray-600">
          Copie e envie pelo WhatsApp ou e-mail.
        </p>
        <p v-else class="text-sm text-gray-600">Convite utilizado — o link e a mensagem abaixo são só para consulta.</p>
        <div>
          <label class="block text-xs font-medium text-gray-500 mb-1">URL</label>
          <div class="flex gap-2">
            <input readonly :value="resultModal.url" class="flex-1 min-w-0 text-sm px-3 py-2 border border-gray-300 rounded-lg bg-gray-50" />
            <button
              v-if="resultModal.inviteStatus !== 'usado'"
              type="button"
              class="px-3 py-2 bg-indigo-600 text-white rounded-lg text-sm shrink-0 shadow-sm transition select-none hover:bg-indigo-700 active:scale-95 active:brightness-95 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-1"
              @click="copyText(resultModal.url, 'url')"
            >
              {{ copyFlash.url ? 'Copiado!' : 'Copiar' }}
            </button>
          </div>
        </div>
        <div>
          <label class="block text-xs font-medium text-gray-500 mb-1">Mensagem sugerida</label>
          <textarea
            readonly
            rows="4"
            :value="resultModal.suggested"
            class="w-full text-sm px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 whitespace-pre-wrap"
          ></textarea>
          <button
            v-if="resultModal.inviteStatus !== 'usado'"
            type="button"
            class="mt-2 px-3 py-2 bg-gray-800 text-white rounded-lg text-sm shadow-sm transition select-none hover:bg-gray-900 active:scale-95 active:brightness-95 focus:outline-none focus:ring-2 focus:ring-gray-600 focus:ring-offset-1"
            @click="copyText(resultModal.suggested, 'msg')"
          >
            {{ copyFlash.msg ? 'Copiado!' : 'Copiar mensagem' }}
          </button>
        </div>
        <button
          type="button"
          class="w-full py-2 border border-gray-300 rounded-lg text-gray-700 shadow-sm transition select-none hover:bg-gray-50 active:scale-[0.99] active:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-1"
          @click="closeResult"
        >
          Fechar
        </button>
      </div>
    </div>

    <!-- Excluir convite (mesmo padrão do CrudBase / imóveis) -->
    <div
      v-if="deleteConfirmItem"
      class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[85] p-4"
      @click.self="closeDeleteConfirm"
    >
      <div class="bg-white rounded-lg shadow-xl max-w-2xl p-6 max-h-[90vh] overflow-y-auto">
        <h3 class="flex items-center text-lg font-semibold text-gray-900 mb-4">
          <svg class="w-6 h-6 text-amber-500 mr-2 shrink-0" fill="currentColor" viewBox="0 0 20 20">
            <path
              fill-rule="evenodd"
              d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l6.518 11.59C19.02 16.92 18.145 18 16.757 18H3.243c-1.388 0-2.263-1.08-1.504-2.31l6.518-11.59zM11 14a1 1 0 10-2 0 1 1 0 002 0zm-1-8a1 1 0 00-.894.553L9 7v4a1 1 0 002 0V7l-.106-.447A1 1 0 0010 6z"
              clip-rule="evenodd"
            />
          </svg>
          Confirmação!
        </h3>
        <p class="text-gray-600 mb-6">
          Tem certeza que deseja excluir este convite?<br />
          Esta ação não pode ser desfeita.
        </p>
        <div class="flex justify-end space-x-3">
          <button
            type="button"
            :disabled="deletingInvite"
            class="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
            @click="executeDeleteInvite"
          >
            {{ deletingInvite ? 'Excluindo...' : 'Sim' }}
          </button>
          <button
            type="button"
            :disabled="deletingInvite"
            class="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition disabled:opacity-50 disabled:cursor-not-allowed"
            @click="closeDeleteConfirm"
          >
            Não
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'

export default {
  name: 'PersonRegistrationInvites',

  data() {
    return {
      maxOpen: 10,
      showFilters: false,
      filters: {
        greeting_name: '',
        status: '',
        user_id: '',
        created_from: '',
        created_to: ''
      },
      filterUsers: [],
      pagination: {
        page: 1,
        pages: 0,
        count: 0,
        from: 0,
        to: 0,
        prev: null,
        next: null
      },
      invites: [],
      loading: true,
      loadError: '',
      openCount: 0,
      modalOpen: false,
      greetingName: '',
      modalError: '',
      creating: false,
      deleteConfirmItem: null,
      deletingInvite: false,
      copyFlash: { url: false, msg: false },
      copyFlashTimers: { url: null, msg: null },
      resultModal: {
        open: false,
        url: '',
        suggested: '',
        greetingName: '',
        statusHint: '',
        inviteStatus: 'aberto'
      }
    }
  },

  mounted() {
    this.loadInvites()
  },

  beforeUnmount() {
    this.clearCopyFlash()
  },

  methods: {
    inviteListParams() {
      const params = { page: this.pagination.page }
      if (this.filters.greeting_name) params.greeting_name = this.filters.greeting_name
      if (this.filters.status) params.status = this.filters.status
      if (this.filters.user_id) params.user_id = this.filters.user_id
      if (this.filters.created_from) params.created_from = this.filters.created_from
      if (this.filters.created_to) params.created_to = this.filters.created_to
      return params
    },

    applyFilters() {
      this.pagination.page = 1
      this.loadInvites()
    },

    changePage(page) {
      const p = Number(page)
      if (!p || p < 1 || (this.pagination.pages && p > this.pagination.pages)) return
      this.pagination.page = p
      this.loadInvites()
    },

    async loadInvites() {
      this.loading = true
      this.loadError = ''
      try {
        const { data } = await axios.get('/api/v1/person_registration_invites', {
          params: this.inviteListParams()
        })
        this.invites = data.data || []
        this.openCount = data.open_invites_for_current_user ?? 0
        if (Array.isArray(data.filter_users)) {
          this.filterUsers = data.filter_users
        }
        if (data.pagy) {
          this.pagination = {
            page: data.pagy.page || 1,
            pages: data.pagy.pages || 0,
            count: data.pagy.count ?? 0,
            from: data.pagy.from ?? 0,
            to: data.pagy.to ?? 0,
            prev: data.pagy.prev ?? null,
            next: data.pagy.next ?? null
          }
        }
      } catch (e) {
        this.loadError = e.response?.data?.error || 'Não foi possível carregar os convites.'
      } finally {
        this.loading = false
      }
    },

    statusClass(status) {
      if (status === 'aberto') return 'bg-green-100 text-green-800'
      if (status === 'usado') return 'bg-gray-100 text-gray-700'
      return 'bg-amber-100 text-amber-800'
    },

    formatDate(iso) {
      if (!iso) return '—'
      const d = new Date(iso)
      if (Number.isNaN(d.getTime())) return iso
      return d.toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' })
    },

    openModal() {
      if (this.openCount >= this.maxOpen) return
      this.modalError = ''
      this.greetingName = ''
      this.modalOpen = true
    },

    closeModal() {
      this.modalOpen = false
    },

    closeResult() {
      this.clearCopyFlash()
      this.resultModal.open = false
    },

    clearCopyFlash() {
      ;[ 'url', 'msg' ].forEach((k) => {
        if (this.copyFlashTimers[k]) {
          clearTimeout(this.copyFlashTimers[k])
          this.copyFlashTimers[k] = null
        }
      })
      this.copyFlash = { url: false, msg: false }
    },

    async openLinkModal(id) {
      try {
        const { data } = await axios.get(`/api/v1/person_registration_invites/${id}`)
        const inv = data.invite || {}
        let hint = ''
        if (inv.status === 'usado') {
          hint = 'Este convite já foi utilizado; o link pode não aceitar novo cadastro.'
        } else if (inv.status === 'expirado') {
          hint = 'Este convite está expirado; o link não funcionará para novos cadastros.'
        }
        this.clearCopyFlash()
        this.resultModal = {
          open: true,
          url: data.url,
          suggested: data.suggested_message,
          greetingName: inv.greeting_name || '',
          statusHint: hint,
          inviteStatus: inv.status || 'aberto'
        }
      } catch (e) {
        window.alert(e.response?.data?.error || 'Não foi possível carregar o convite.')
      }
    },

    openDeleteConfirm(row) {
      this.deleteConfirmItem = row
    },

    closeDeleteConfirm() {
      if (this.deletingInvite) return
      this.deleteConfirmItem = null
    },

    async executeDeleteInvite() {
      if (!this.deleteConfirmItem) return
      this.deletingInvite = true
      try {
        await axios.delete(`/api/v1/person_registration_invites/${this.deleteConfirmItem.id}`)
        this.deleteConfirmItem = null
        await this.loadInvites()
      } catch (e) {
        window.alert(e.response?.data?.error || 'Não foi possível excluir o convite.')
      } finally {
        this.deletingInvite = false
      }
    },

    async submitInvite() {
      if (!this.greetingName) return
      this.modalError = ''
      this.creating = true
      try {
        const { data } = await axios.post('/api/v1/person_registration_invites', {
          person_registration_invite: { greeting_name: this.greetingName }
        })
        this.modalOpen = false
        const inv = data.invite || {}
        this.clearCopyFlash()
        this.resultModal = {
          open: true,
          url: data.url,
          suggested: data.suggested_message,
          greetingName: inv.greeting_name || this.greetingName,
          statusHint: '',
          inviteStatus: inv.status || 'aberto'
        }
        this.pagination.page = 1
        await this.loadInvites()
      } catch (e) {
        const errs = e.response?.data?.errors
        this.modalError = Array.isArray(errs) ? errs.join(', ') : e.response?.data?.error || 'Não foi possível gerar o convite.'
      } finally {
        this.creating = false
      }
    },

    async copyText(text, kind) {
      if (!text) return
      try {
        await navigator.clipboard.writeText(text)
        if (kind === 'url' || kind === 'msg') {
          if (this.copyFlashTimers[kind]) clearTimeout(this.copyFlashTimers[kind])
          this.copyFlash = { ...this.copyFlash, [kind]: true }
          this.copyFlashTimers[kind] = setTimeout(() => {
            this.copyFlash = { ...this.copyFlash, [kind]: false }
            this.copyFlashTimers[kind] = null
          }, 2000)
        }
      } catch {
        window.prompt('Copie:', text)
      }
    }
  }
}
</script>